import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useProductCatalog } from '@/context/ProductCatalogContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';

export default function ProductShowcase() {
  const { products } = useProductCatalog();
  const lithium = products.find((p) => p.category === 'lithium' && p.id === 'sr-li-100');
  const agm = products.find((p) => p.category === 'agm' && p.id === 'sr-agm-100');
  const gel = products.find((p) => p.category === 'gel' && p.id === 'sr-gel-120');
  const { theme } = useTheme();
  const { settings } = useBadgeVisibility();
  const light = theme === 'light';

  const sectionBg = light ? 'bg-gray-50' : 'bg-[#0D1B2A]';
  const headingColor = light ? 'text-gray-900' : 'text-white';
  const mutedHeading = light ? 'text-gray-400' : 'text-white/60';
  const card1Bg = light ? 'bg-gray-200' : 'bg-[#1C2B3A]';
  const card2Bg = light ? 'bg-gray-300' : 'bg-[#232F3E]';
  const card3Bg = light ? 'bg-gray-100 border border-gray-300' : 'bg-[#0D1B2A] border border-white/10';
  const overlay1 = light ? 'bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent' : 'bg-gradient-to-t from-[#0D1B2A] via-[#0D1B2A]/40 to-transparent';
  const overlay2 = light ? 'bg-gradient-to-t from-gray-800 via-gray-800/60 to-transparent' : 'bg-gradient-to-t from-[#232F3E] via-[#232F3E]/60 to-transparent';
  const overlay3 = light ? 'bg-gradient-to-t from-gray-700 via-gray-700/60 to-transparent' : 'bg-gradient-to-t from-[#0D1B2A] via-[#0D1B2A]/60 to-transparent';
  const subText = light ? 'text-white/60' : 'text-white/50';
  const seriesBadgeCls = 'inline-block text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide';

  return (
    <section className={`${sectionBg} w-full py-20 transition-colors duration-300`}>
      <div className="w-full px-6 md:px-10">
        {/* Header */}
        <div className="mb-12">
          {settings.productBadges && (
            <span
              className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', color: 'var(--accent)' }}
            >
              Our Lineup
            </span>
          )}
          <h2 className={`text-4xl md:text-5xl font-black ${headingColor} leading-tight`}>
            Three Technologies.<br />
            <span className={mutedHeading}>One Mission.</span>
          </h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Lithium — Large Card */}
          {lithium && (
            <div className={`lg:col-span-2 relative ${card1Bg} rounded-lg overflow-hidden group cursor-pointer min-h-[420px]`}>
              <img
                src={lithium.image}
                alt={lithium.name}
                className="absolute inset-0 w-full h-full object-cover object-top opacity-40 group-hover:opacity-50 transition-opacity duration-500"
              />
              <div className={`absolute inset-0 ${overlay1}`}></div>
              <div className="relative z-10 p-8 h-full flex flex-col justify-end">
                <div className="mb-2">
                  {settings.productBadges && (
                    <span className={seriesBadgeCls} style={{ backgroundColor: 'var(--accent)' }}>
                      Lithium LiFePO4
                    </span>
                  )}
                </div>
                <h3 className="text-3xl font-black text-white mb-2">The Future of<br />Marine Power</h3>
                <p className="text-white/60 text-sm mb-6 max-w-md">
                  Up to 10x longer life than lead-acid. Half the weight. Zero maintenance. The ultimate upgrade for serious boaters.
                </p>
                <div className="flex items-center gap-6 mb-6">
                  <div>
                    <p className="text-2xl font-black" style={{ color: 'var(--accent)' }}>2000+</p>
                    <p className={`${subText} text-xs`}>Charge Cycles</p>
                  </div>
                  <div className="w-px h-10 bg-white/20"></div>
                  <div>
                    <p className="text-2xl font-black" style={{ color: 'var(--accent)' }}>50%</p>
                    <p className={`${subText} text-xs`}>Lighter than AGM</p>
                  </div>
                  <div className="w-px h-10 bg-white/20"></div>
                  <div>
                    <p className="text-2xl font-black" style={{ color: 'var(--accent)' }}>10yr</p>
                    <p className={`${subText} text-xs`}>Design Life</p>
                  </div>
                </div>
                <Link
                  to="/products?cat=lithium"
                  className="inline-flex items-center gap-2 font-bold text-sm hover:gap-3 transition-all cursor-pointer whitespace-nowrap"
                  style={{ color: 'var(--accent)' }}
                >
                  Explore Lithium Series <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          )}

          {/* AGM + Gel stacked */}
          <div className="flex flex-col gap-4">
            {agm && (
              <div className={`relative ${card2Bg} rounded-lg overflow-hidden group cursor-pointer flex-1 min-h-[200px]`}>
                <img
                  src={agm.image}
                  alt={agm.name}
                  className="absolute inset-0 w-full h-full object-cover object-top opacity-30 group-hover:opacity-40 transition-opacity duration-500"
                />
                <div className={`absolute inset-0 ${overlay2}`}></div>
                <div className="relative z-10 p-6 h-full flex flex-col justify-end">
                  {settings.productBadges && (
                    <div className="mb-2">
                      <span className={seriesBadgeCls} style={{ backgroundColor: 'var(--accent)' }}>
                        AGM Series
                      </span>
                    </div>
                  )}
                  <h3 className="text-xl font-black text-white mb-1">Proven Reliability</h3>
                  <p className="text-white/50 text-xs mb-4">Maintenance-free, dual-purpose power for every vessel.</p>
                  <Link
                    to="/products?cat=agm"
                    className="inline-flex items-center gap-1 font-bold text-sm hover:gap-2 transition-all cursor-pointer whitespace-nowrap"
                    style={{ color: 'var(--accent)' }}
                  >
                    Explore AGM Series <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            )}
            {gel && (
              <div className={`relative ${card3Bg} rounded-lg overflow-hidden group cursor-pointer flex-1 min-h-[200px]`}>
                <img
                  src={gel.image}
                  alt={gel.name}
                  className="absolute inset-0 w-full h-full object-cover object-top opacity-30 group-hover:opacity-40 transition-opacity duration-500"
                />
                <div className={`absolute inset-0 ${overlay3}`}></div>
                <div className="relative z-10 p-6 h-full flex flex-col justify-end">
                  {settings.productBadges && (
                    <div className="mb-2">
                      <span className={seriesBadgeCls} style={{ backgroundColor: 'var(--accent)' }}>
                        Gel Series
                      </span>
                    </div>
                  )}
                  <h3 className="text-xl font-black text-white mb-1">Electronics Safe</h3>
                  <p className="text-white/50 text-xs mb-4">Ultra-stable voltage for sensitive marine electronics.</p>
                  <Link
                    to="/products?cat=gel"
                    className="inline-flex items-center gap-1 font-bold text-sm hover:gap-2 transition-all cursor-pointer whitespace-nowrap"
                    style={{ color: 'var(--accent)' }}
                  >
                    Explore Gel Series <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
